import Image from "next/image";
import type { ReactNode } from "react";

import type { AiAnswer } from "./ai-value-demo-copy";

type CcAiAnswerProps = {
  /** The AI service's name and logo, shown in the top bar. */
  service: string;
  logo: string;
  /** "Before" / "After", read out first (the visible tags are decorative). */
  stateLabel: string;
  question: string;
  answer: AiAnswer;
  /** Company name that gets a teal highlight wherever it appears. */
  highlight: string;
  labels: { question: string; answer: string; sources: string };
};

/** Wraps every occurrence of `term` in a highlight. */
function withHighlight(text: string, term: string): ReactNode {
  if (!term || !text.includes(term)) return text;
  return text.split(term).flatMap((part, index) =>
    index === 0
      ? [part]
      : [
          <mark key={index} className="cc-ai-answer__mark">
            {term}
          </mark>,
          part,
        ],
  );
}

/**
 * A neutral chat-style answer card (not any service's real interface): the
 * question as a bubble, the written answer with a short list, and the cited
 * sources as chips. Used as one layer of the before/after comparison.
 */
export function CcAiAnswer({
  service,
  logo,
  stateLabel,
  question,
  answer,
  highlight,
  labels,
}: CcAiAnswerProps) {
  const brandKey = highlight.split(" ")[0]?.toLowerCase() ?? "";

  return (
    <div className="cc-ai-answer">
      <div className="cc-ai-answer__bar">
        <span className="cc-ai-answer__service">
          <Image src={logo} alt="" width={16} height={16} unoptimized />
          {service}
        </span>
        <span className="cc-ai-answer__state">{stateLabel}</span>
      </div>

      <div className="cc-ai-answer__body">
        <p className="cc-ai-answer__sr">{`${stateLabel}, ${service}`}</p>
        <p className="cc-ai-answer__question">
          <span className="cc-ai-answer__sr">{`${labels.question}: `}</span>
          {question}
        </p>

        <div className="cc-ai-answer__reply">
          <span className="cc-ai-answer__sr">{`${labels.answer}: `}</span>
          <p>{withHighlight(answer.intro, highlight)}</p>
          <ol>
            {answer.items.map((item) => (
              <li key={item}>{withHighlight(item, highlight)}</li>
            ))}
          </ol>
        </div>

        {answer.sources.length > 0 ? (
          <div className="cc-ai-answer__sources">
            <span className="cc-ai-answer__sources-label">{labels.sources}</span>
            <ul>
              {answer.sources.map((source) => (
                <li
                  key={source}
                  className={
                    brandKey && source.toLowerCase().includes(brandKey)
                      ? "is-brand"
                      : undefined
                  }
                >
                  {source}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
